import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function TermsConditions() {

  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Fetch Terms
  const fetchTerms = async () => {
    try {

      const res = await fetch(`${API_BASE_URL}/api/terms/all`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();
      setTerms(data);

    } catch {
      toast.error("Failed to load terms");
    }
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  // 🔐 Admin Auth
  useEffect(() => {

    const roles = JSON.parse(localStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("Login required");
      navigate("/");
      return;
    }

    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("Not authorized");
      navigate("/");
      return;
    }

  }, [navigate]);

  // Add Term
  const handleAdd = async () => {

    if (!newTerm.trim()) {
      toast.warning("Enter term text");
      return;
    }

    try {

      const res = await fetch(`${API_BASE_URL}/api/terms/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ text: newTerm })
      });

      if (!res.ok) throw new Error();

      toast.success("Term added");
      setNewTerm("");
      fetchTerms();

    } catch {
      toast.error("Failed to add term");
    }

  };

  // Update
  const handleUpdate = async (id) => {

    if (!editText.trim()) {
      toast.warning("Enter valid text");
      return;
    }

    try {

      const res = await fetch(`${API_BASE_URL}/api/terms/update/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ text: editText })
      });

      if (!res.ok) throw new Error();

      toast.success("Term updated");
      setEditingId(null);
      fetchTerms();

    } catch {
      toast.error("Update failed");
    }

  };

  // Delete
  const handleDelete = async (id) => {

    if (!window.confirm("Delete this term?")) return;

    try {

      const res = await fetch(`${API_BASE_URL}/api/terms/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error();

      toast.info("Term deleted");
      fetchTerms();

    } catch {
      toast.error("Delete failed");
    }

  };

  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div
        className="d-flex flex-column p-4"
        style={{
          minHeight: "100vh",
          backgroundColor: "#F5F7FB",
          marginTop: "110px",
        }}
      >
        <h3 className="fw-bold text-dark mb-4 text-start">
          Terms & Conditions Management
        </h3>

        <div className="row flex-grow-1 g-4">

          {/* Add Terms */}
          <div className="col-md-4 d-flex">

            <div className="card shadow-sm border-0 flex-fill"
              style={{
                borderRadius: "12px",
                height: "fit-content",
                alignSelf: "flex-start"
              }}
            >

              <div className="card-header fw-semibold"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px"
                }}>
                Add New Term
              </div>

              <div className="card-body">

                <textarea
                  className="form-control mb-3"
                  placeholder="Enter terms text"
                  rows="4"
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                />

                <button
                  className="btn text-dark w-40"
                  style={{ backgroundColor: "#DFE4EB" }}
                  onClick={handleAdd}
                >
                  <FaPlus className="me-2" />
                  Add Term
                </button>

              </div>
            </div>

          </div>

          {/* Terms List */}
          <div className="col-md-8 d-flex">

            <div className="card shadow-sm border-0 flex-fill"
              style={{ borderRadius: "12px" }}
            >

              <div className="card-header fw-semibold d-flex justify-content-between"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px"
                }}
              >
                <span>All Terms</span>
                <span className="badge bg-secondary">
                  {terms.length} Total
                </span>
              </div>

              <div className="card-body p-0"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto"
                }}
              >

                <ul className="list-group list-group-flush">

                  {terms.length === 0 &&
                    <li className="list-group-item text-center text-muted">
                      No terms added yet
                    </li>
                  }

                  {terms.map(term => (

                    <li
                      key={term.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >

                      {editingId === term.id ? (
                        <textarea
                          className="form-control me-2"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          style={{ maxWidth: "350px" }}
                        />
                      ) : (
                        <span>{term.text}</span>
                      )}

                      <div>

                        {editingId === term.id ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-2"
                              onClick={() => handleUpdate(term.id)}
                            >
                              <FaSave />
                            </button>

                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn btn-sm me-2"
                              onClick={() => {
                                setEditingId(term.id);
                                setEditText(term.text);
                              }}
                            >
                              <FaEdit />
                            </button>

                            <button
                              className="btn btn-sm"
                              onClick={() => handleDelete(term.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}

                      </div>

                    </li>

                  ))}

                </ul>

              </div>

            </div>

          </div>

        </div>

      </div>
    </>
  );
}